import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import type { Errors } from '@inertiajs/core'
import { ImagePlus, Package, Pencil, Trash2, Wrench, X } from 'lucide-react'
import { Field, TextArea, TextInput } from '@/components/explore/CreatorFields'
import { formatBRL } from '@/components/explore/PriceSummary'
import { cn } from '@/lib/utils'
import type { CatalogItem } from './types'

const MAX_PHOTOS = 8
const MAX_PHOTO_MB = 10
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

type Draft = {
  kind: CatalogItem['kind']
  name: string
  description: string
  price: string
  unit: string
}

const emptyDraft = (kind: CatalogItem['kind']): Draft => ({
  kind,
  name: '',
  description: '',
  price: '',
  unit: '',
})

/**
 * Passo 4 — o catálogo: o que o negócio vende e o que ele faz.
 *
 * Ao contrário do resto do wizard, este passo NÃO passa pelo formulário da
 * página: cada item é um registro próprio, com endpoint próprio, porque tem
 * arquivo junto. Foto precisa de um registro salvo para ser anexada — é
 * exatamente por isso que o wizard grava rascunho desde o passo 1.
 *
 * O passo é pulável. Negócio sem catálogo publica do mesmo jeito, e obrigar a
 * cadastrar um produto para poder continuar transformaria a parte mais rica da
 * página numa pedágio.
 */
export default function StepCatalog({
  pageId,
  items,
}: {
  pageId: number
  items: CatalogItem[]
}) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [photos, setPhotos] = useState<File[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const abrirNovo = (kind: CatalogItem['kind']) => {
    setEditingId(null)
    setPhotos([])
    setErro(null)
    setDraft(emptyDraft(kind))
  }

  const abrirEdicao = (item: CatalogItem) => {
    setEditingId(item.id)
    setPhotos([])
    setErro(null)
    setDraft({
      kind: item.kind,
      name: item.name,
      description: item.description,
      // O input fala em reais; a API converte para centavos.
      price: item.price == null ? '' : item.price.toFixed(2).replace('.', ','),
      unit: item.unit,
    })
  }

  const fechar = () => {
    setDraft(null)
    setEditingId(null)
    setPhotos([])
    setErro(null)
  }

  // Mesma checagem do servidor, só que antes de gastar o upload: o arquivo
  // grande demais só seria recusado depois de subir inteiro.
  const escolherFotos = (lista: FileList | null) => {
    if (!lista) return
    const escolhidas = Array.from(lista)
    const invalida = escolhidas.find(
      (f) =>
        !PHOTO_TYPES.includes(f.type) || f.size > MAX_PHOTO_MB * 1024 * 1024,
    )
    if (invalida) {
      setErro(
        !PHOTO_TYPES.includes(invalida.type)
          ? `${invalida.name}: envie JPEG, PNG, WebP ou GIF.`
          : `${invalida.name}: imagem muito grande (máximo ${MAX_PHOTO_MB}MB).`,
      )
      return
    }
    setErro(null)
    setPhotos((atuais) => [...atuais, ...escolhidas].slice(0, MAX_PHOTOS))
    if (fileRef.current) fileRef.current.value = ''
  }

  const salvar = () => {
    if (!draft || draft.name.trim().length === 0) return

    setSalvando(true)
    const payload = { ...draft, photos }
    const opcoes = {
      // Tem arquivo junto: sem isto o Inertia manda JSON e as fotos ficam pelo
      // caminho.
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => fechar(),
      onError: (errors: Errors) => {
        // O Rails devolve todas as mensagens de um campo; a primeira é a que a
        // pessoa precisa corrigir agora.
        const primeiro = Object.values(errors)[0]
        setErro(
          (Array.isArray(primeiro) ? primeiro[0] : primeiro) ??
            'Não foi possível salvar o item.',
        )
      },
      onFinish: () => setSalvando(false),
    }

    if (editingId) {
      router.patch(`/nova-pagina/${pageId}/itens/${editingId}`, payload, opcoes)
    } else {
      router.post(`/nova-pagina/${pageId}/itens`, payload, opcoes)
    }
  }

  const remover = (item: CatalogItem) => {
    router.delete(`/nova-pagina/${pageId}/itens/${item.id}`, {
      preserveScroll: true,
    })
  }

  const removerFoto = (item: CatalogItem, photoId: number) => {
    router.delete(`/nova-pagina/${pageId}/itens/${item.id}/fotos/${photoId}`, {
      preserveScroll: true,
    })
  }

  return (
    <div className="space-y-5">
      <p className="max-w-2xl text-sm text-gray-600">
        Cadastre o que você vende e o que você faz, com foto e preço. É o que a
        pessoa mais olha antes de chamar no WhatsApp — e é o material que nossa
        IA usa para escrever sua página. Pode pular e voltar depois.
      </p>

      <div className="flex flex-wrap gap-3">
        <AddButton
          icon={<Wrench className="size-4" aria-hidden="true" />}
          label="Adicionar serviço"
          onClick={() => abrirNovo('servico')}
        />
        <AddButton
          icon={<Package className="size-4" aria-hidden="true" />}
          label="Adicionar produto"
          onClick={() => abrirNovo('produto')}
        />
      </div>

      {draft && (
        <ItemForm
          draft={draft}
          setDraft={setDraft}
          photos={photos}
          onPickPhotos={escolherFotos}
          onDropPhoto={(index) =>
            setPhotos((atuais) => atuais.filter((_, i) => i !== index))
          }
          fileRef={fileRef}
          erro={erro}
          salvando={salvando}
          onSave={salvar}
          onCancel={fechar}
          editando={editingId != null}
        />
      )}

      {items.length === 0 && !draft ? (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
          Nada cadastrado ainda.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-3 rounded-xl border border-gray-200 p-3"
            >
              <ItemThumb item={item} onRemovePhoto={removerFoto} />

              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  {item.name}
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.6875rem] font-medium text-gray-500">
                    {item.kind === 'produto' ? 'Produto' : 'Serviço'}
                  </span>
                </p>
                {item.description && (
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                    {item.description}
                  </p>
                )}
                <p className="mt-1 text-sm font-bold text-gray-900">
                  {item.price == null ? (
                    <span className="text-xs font-medium text-gray-500">
                      Sob consulta
                    </span>
                  ) : (
                    <>
                      {formatBRL(item.price)}
                      {item.unit && (
                        <span className="text-xs font-medium text-gray-500">
                          {' '}
                          {item.unit}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>

              <div className="flex shrink-0 gap-1">
                <IconButton
                  label={`Editar ${item.name}`}
                  onClick={() => abrirEdicao(item)}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </IconButton>
                <IconButton
                  label={`Remover ${item.name}`}
                  onClick={() => remover(item)}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AddButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-dashed border-gray-300 px-4 py-2.5',
        'text-sm font-semibold text-gray-700 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
    >
      {children}
    </button>
  )
}

/** A miniatura do item na lista, com o X para tirar a foto já salva. */
function ItemThumb({
  item,
  onRemovePhoto,
}: {
  item: CatalogItem
  onRemovePhoto: (item: CatalogItem, photoId: number) => void
}) {
  const capa = item.photos[0]

  if (!capa) {
    return (
      <span className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-300">
        <ImagePlus className="size-5" aria-hidden="true" />
      </span>
    )
  }

  return (
    <span className="relative block size-16 shrink-0">
      <img
        src={capa.url}
        alt=""
        className="size-16 rounded-lg object-cover"
        loading="lazy"
      />
      <button
        type="button"
        onClick={() => onRemovePhoto(item, capa.id)}
        aria-label={`Remover foto de ${item.name}`}
        className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-400 shadow ring-1 ring-gray-200 transition-colors hover:text-red-600"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
      {item.photos.length > 1 && (
        <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 text-[0.625rem] font-semibold text-white">
          +{item.photos.length - 1}
        </span>
      )}
    </span>
  )
}

function ItemForm({
  draft,
  setDraft,
  photos,
  onPickPhotos,
  onDropPhoto,
  fileRef,
  erro,
  salvando,
  onSave,
  onCancel,
  editando,
}: {
  draft: Draft
  setDraft: (d: Draft) => void
  photos: File[]
  onPickPhotos: (files: FileList | null) => void
  onDropPhoto: (index: number) => void
  fileRef: React.RefObject<HTMLInputElement | null>
  erro: string | null
  salvando: boolean
  onSave: () => void
  onCancel: () => void
  editando: boolean
}) {
  const produto = draft.kind === 'produto'

  return (
    <div className="space-y-3 rounded-xl border border-primary/40 bg-primary/[0.03] p-4">
      <p className="text-sm font-bold text-gray-900">
        {editando ? 'Editar' : produto ? 'Novo produto' : 'Novo serviço'}
      </p>

      <Field label={produto ? 'Nome do produto' : 'Nome do serviço'} required>
        <TextInput
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
          placeholder={
            produto ? 'Digite o nome do produto' : 'Digite o nome do serviço'
          }
        />
      </Field>

      <Field label="Descrição (opcional)">
        <TextArea
          value={draft.description}
          onChange={(v) => setDraft({ ...draft, description: v })}
          rows={3}
          placeholder="Conte em poucas linhas o que está incluso"
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Preço (opcional)"
          hint="Deixe em branco para aparecer como “sob consulta”."
        >
          <TextInput
            value={draft.price}
            onChange={(v) => setDraft({ ...draft, price: v })}
            placeholder="Digite o valor em reais"
            inputMode="decimal"
          />
        </Field>
        <Field label="Unidade (opcional)" hint="Ex.: por aula, por diária">
          <TextInput
            value={draft.unit}
            onChange={(v) => setDraft({ ...draft, unit: v })}
            placeholder="Digite a unidade de cobrança"
          />
        </Field>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700">Fotos (opcional)</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {photos.map((foto, index) => (
            <span key={`${foto.name}-${index}`} className="relative block">
              <img
                src={URL.createObjectURL(foto)}
                alt=""
                className="size-16 rounded-lg object-cover"
              />
              <button
                type="button"
                onClick={() => onDropPhoto(index)}
                aria-label={`Tirar ${foto.name}`}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-400 shadow ring-1 ring-gray-200 transition-colors hover:text-red-600"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            </span>
          ))}

          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex size-16 items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-400 transition-colors hover:border-primary hover:text-primary"
              aria-label="Adicionar fotos"
            >
              <ImagePlus className="size-5" aria-hidden="true" />
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept={PHOTO_TYPES.join(',')}
            multiple
            onChange={(e) => onPickPhotos(e.target.files)}
            className="hidden"
          />
        </div>
      </div>

      {erro && <p className="text-xs text-red-600">{erro}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={salvando || draft.name.trim().length === 0}
          className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {salvando ? 'Salvando…' : 'Salvar item'}
        </button>
      </div>
    </div>
  )
}
